import { useState, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { createNotification } from '../../../lib/notifications/createNotification';
import { setzeTicketStatus } from '../../../lib/tickets/status';
import type { Ticket, TicketEditData, HistorieMini } from './types';

// Geteilte Edit/Save/Status-Orchestrierung des Ticket-Detail-Panels (siehe
// .superpowers/sdd/task-6-report.md). Extrahiert aus TicketDetailsSidebar.tsx, damit Panel
// UND die neue Detail-Seite (Task 7) EINE Kopie des atomaren `handleSave` (tickets.update +
// entity_links-Dual-Write + ticket_historie + Assignee-Notification) und des Status-Flows
// teilen, statt zu duplizieren. Feld-/Handler-Namen 1:1 aus dem Panel übernommen.
export interface TicketBearbeitung {
  editing: boolean;
  setEditing: (b: boolean) => void;
  saving: boolean;
  editData: TicketEditData;
  setEditData: Dispatch<SetStateAction<TicketEditData>>;
  handleSave: () => Promise<void>;
  /** Einzelne Felder sofort speichern (gleicher Schreibweg wie handleSave, Bearbeiten-Modus bleibt unberührt).
   *  Liefert false bei Fehler. Für „Übernehmen“ und das Bearbeiten direkt in den Widgets. */
  speichereFelder: (teil: Partial<TicketEditData>) => Promise<boolean>;
  /** Status direkt setzen (ohne Abschluss-Prüfung; die macht handleStatusChange). */
  setzeStatus: (neuerStatus: string) => Promise<void>;
  handleStatusChange: (newStatus: string) => void;   // inkl. Abschluss-Guard
  /** Zielstatus des offenen Abschluss-Dialogs (erledigt oder geschlossen) */
  abschlussZiel: string;
  // Abschluss-Dialog (Namen 1:1 aus dem Panel: showAbschluss = showAbschlussDialog,
  // handleAbschluss = confirm, setShowAbschluss(false) = cancel). JSX bleibt im Consumer.
  showAbschluss: boolean;
  setShowAbschluss: (b: boolean) => void;
  abschlussGrund: string;
  setAbschlussGrund: (s: string) => void;
  handleAbschluss: () => void;
  /** Abschluss mit Grund aus dem Dialog (Ziel = abschlussZiel) */
  handleAbschlussMitGrund: (grund: string) => void;
  // Mini-Timeline: State + Fetch mussten mitwandern, weil handleSave/updateStatus/Reset-Effect
  // `await fetchMiniHistorie()` VOR `onUpdate()` aufrufen (mit try/catch). Bei der fixen
  // 3-arg-Signatur ist das nur byte-genau, wenn der Fetch im Hook lebt; das Timeline-JSX
  // liest `miniHistorie` weiterhin im Consumer.
  miniHistorie: HistorieMini[];
  fetchMiniHistorie: () => Promise<void>;
}

export function useTicketBearbeitung(ticket: Ticket, onUpdate: () => void, userProfile?: any): TicketBearbeitung {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState<TicketEditData>({
    titel: ticket.titel,
    beschreibung: ticket.beschreibung || '',
    prioritaet: ticket.prioritaet,
    kategorie_id: ticket.kategorie_id || '',
    zustaendig_id: ticket.zustaendig_id || '',
    zustaendig_typ: ticket.zustaendig_typ ?? null,
    faellig_am: ticket.faellig_am || '',
    wiedervorlage_am: ticket.wiedervorlage_am || '',
    wiedervorlage_notiz: ticket.wiedervorlage_notiz || '',
    objekt_id: ticket.objekt_id || '',
    mieter_id: ticket.mieter_id || '',
    eigentuemer_id: ticket.eigentuemer_id || '',
  });

  // Live-Referenzen auf Formular- und Ticket-Stand. Die Speichern-/Status-Buttons liegen in der
  // Dynamic Island, und usePageControls registriert das Controls-Element nur, wenn sich seine
  // (primitiven) Deps ändern — editData gehört bewusst nicht dazu. Das dort eingefrorene
  // handleSave würde sonst den editData-Stand vom Registrier-Zeitpunkt (= Klick auf „Bearbeiten")
  // schreiben, also die unveränderten Werte: Fälligkeit & Co. kamen nie in der DB an
  // (updated_at sprang, Feld blieb null, keine Verlaufs-Zeile). Über die Refs lesen die Handler
  // immer den aktuellen Stand, egal aus welchem Render sie aufgerufen werden.
  const editDataRef = useRef(editData);
  editDataRef.current = editData;
  const ticketRef = useRef(ticket);
  ticketRef.current = ticket;

  // Abschluss-Dialog (Ziel merken: „Geschlossen“ darf nicht zu „Erledigt“ werden)
  const [showAbschluss, setShowAbschluss] = useState(false);
  const [abschlussZiel, setAbschlussZiel] = useState('erledigt');
  const [abschlussGrund, setAbschlussGrund] = useState('');

  // Mini-Timeline (letzte 3 Verlaufs-Einträge)
  const [miniHistorie, setMiniHistorie] = useState<HistorieMini[]>([]);

  const fetchMiniHistorie = async () => {
    const { data } = await supabase
      .from('ticket_historie')
      .select('id, aktion, alter_wert, neuer_wert, created_at')
      .eq('ticket_id', ticket.id)
      .neq('aktion', 'kommentar')
      .order('created_at', { ascending: false })
      .limit(3);
    setMiniHistorie(data || []);
  };

  useEffect(() => {
    setEditData({
      titel: ticket.titel,
      beschreibung: ticket.beschreibung || '',
      prioritaet: ticket.prioritaet,
      kategorie_id: ticket.kategorie_id || '',
      zustaendig_id: ticket.zustaendig_id || '',
      zustaendig_typ: ticket.zustaendig_typ ?? null,
      faellig_am: ticket.faellig_am || '',
      wiedervorlage_am: ticket.wiedervorlage_am || '',
      wiedervorlage_notiz: ticket.wiedervorlage_notiz || '',
      objekt_id: ticket.objekt_id || '',
      mieter_id: ticket.mieter_id || '',
    eigentuemer_id: ticket.eigentuemer_id || '',
    });
    setEditing(false);
    fetchMiniHistorie();
  }, [ticket.id]);

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === ticket.status) return;

    // Abschluss-Dialog für erledigt/geschlossen
    if ((newStatus === 'erledigt' || newStatus === 'geschlossen') && !ticket.abschluss_grund) {
      setAbschlussZiel(newStatus);
      setAbschlussGrund('');
      setShowAbschluss(true);
      return;
    }

    await updateStatus(newStatus);
  };

  const updateStatus = async (newStatus: string, grund?: string) => {
    try {
      await setzeTicketStatus(ticket, newStatus, { benutzerId: userProfile?.id ?? null, grund });
      setShowAbschluss(false);
      try { await fetchMiniHistorie(); } catch (e) { console.warn('Mini-Historie Refresh fehlgeschlagen', e); }
      onUpdate();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleAbschluss = () => {
    updateStatus(abschlussZiel, abschlussGrund);
  };

  // Gemeinsamer Schreibweg: tickets.update + entity_links-Dual-Write + Historie + Benachrichtigung.
  // Wirft bei Fehler; Aufrufer entscheiden über Edit-Modus und Meldung.
  const schreibe = async (daten: TicketEditData) => {
    const aktuell = ticketRef.current;
    {
      const changes: string[] = [];
      if (daten.titel !== aktuell.titel) changes.push('Titel');
      if (daten.prioritaet !== aktuell.prioritaet) changes.push('Priorität');
      if (daten.kategorie_id !== (aktuell.kategorie_id || '')) changes.push('Kategorie');
      if (daten.zustaendig_id !== (aktuell.zustaendig_id || '')) changes.push('Zuständigkeit');
      if (daten.objekt_id !== (aktuell.objekt_id || '')) changes.push('Objekt');
      if (daten.mieter_id !== (aktuell.mieter_id || '')) changes.push('Mieter');
      if ((daten.eigentuemer_id ?? '') !== (aktuell.eigentuemer_id || '')) changes.push('Eigentümer');
      if (daten.faellig_am !== (aktuell.faellig_am || '')) changes.push('Fälligkeit');
      if (daten.wiedervorlage_am !== (aktuell.wiedervorlage_am || '')) changes.push('Wiedervorlage');
      if (daten.wiedervorlage_notiz !== (aktuell.wiedervorlage_notiz || '')) changes.push('Wiedervorlage-Notiz');

      // Zuständigkeit kann Mitarbeiter ODER Team sein (Typ kommt aus dem Dropdown in editData).
      const zustTyp = daten.zustaendig_id ? (daten.zustaendig_typ ?? 'mitarbeiter') : null;

      // Bewusst kein `eskaliert`/`eskaliert_am`/`status` in diesem Payload (nicht Teil von
      // TicketEditData): die Eskalations-Stufe wird ausschließlich von checkEskalation()
      // (TicketListe.tsx) bzw. dem Status-Dropdown (handleStatusChange) gesetzt. Speichern der
      // Fälligkeit/Wiedervorlage darf sie nie mitverändern — Feldliste hier absichtlich explizit,
      // nicht per Objekt-Spread aus `ticket`.
      const { error: updateError } = await supabase.from('tickets').update({
        titel: daten.titel,
        beschreibung: daten.beschreibung || null,
        prioritaet: daten.prioritaet,
        kategorie_id: daten.kategorie_id || null,
        zustaendig_id: daten.zustaendig_id || null,
        zustaendig_typ: zustTyp,
        faellig_am: daten.faellig_am || null,
        wiedervorlage_am: daten.wiedervorlage_am || null,
        wiedervorlage_notiz: daten.wiedervorlage_notiz || null,
        objekt_id: daten.objekt_id || null,
        mieter_id: daten.mieter_id || null,
        eigentuemer_id: daten.eigentuemer_id || null,
      }).eq('id', aktuell.id);
      // Fehler nicht mehr verschlucken: ein stiller Fehlschlag sieht in der UI exakt so aus wie
      // ein erfolgreiches Speichern (Edit-Modus zu, alte Werte) — genau das war hier das Problem.
      if (updateError) throw updateError;

      // Dual-Write: Zuständigkeit in entity_links (Team → target_type 'team', sonst 'profile')
      if (daten.zustaendig_id !== (aktuell.zustaendig_id || '')) {
        await supabase.from('entity_links').delete()
          .eq('source_type', 'ticket').eq('source_id', aktuell.id).eq('link_type', 'zustaendigkeit');
        if (daten.zustaendig_id) {
          await supabase.from('entity_links').insert({
            source_type: 'ticket', source_id: aktuell.id,
            target_type: zustTyp === 'team' ? 'team' : 'profile', target_id: daten.zustaendig_id,
            link_type: 'zustaendigkeit',
          });
        }
      }

      // Dual-Write: Objekt-Zuordnung in entity_links
      if (daten.objekt_id !== (aktuell.objekt_id || '')) {
        await supabase.from('entity_links').delete()
          .eq('source_type', 'ticket').eq('source_id', aktuell.id).eq('link_type', 'zuordnung').eq('target_type', 'objekt');
        if (daten.objekt_id) {
          await supabase.from('entity_links').insert({
            source_type: 'ticket', source_id: aktuell.id,
            target_type: 'objekt', target_id: daten.objekt_id,
            link_type: 'zuordnung',
          });
        }
      }

      // Dual-Write: Mieter-Zuordnung in entity_links
      if (daten.mieter_id !== (aktuell.mieter_id || '')) {
        await supabase.from('entity_links').delete()
          .eq('source_type', 'ticket').eq('source_id', aktuell.id).eq('link_type', 'zuordnung').eq('target_type', 'mieter');
        if (daten.mieter_id) {
          await supabase.from('entity_links').insert({
            source_type: 'ticket', source_id: aktuell.id,
            target_type: 'mieter', target_id: daten.mieter_id,
            link_type: 'zuordnung',
          });
        }
      }

      // Dual-Write: Eigentümer-Zuordnung in entity_links (#513)
      if ((daten.eigentuemer_id ?? '') !== (aktuell.eigentuemer_id || '')) {
        await supabase.from('entity_links').delete()
          .eq('source_type', 'ticket').eq('source_id', aktuell.id).eq('link_type', 'zuordnung').eq('target_type', 'eigentuemer');
        if (daten.eigentuemer_id) {
          await supabase.from('entity_links').insert({
            source_type: 'ticket', source_id: aktuell.id,
            target_type: 'eigentuemer', target_id: daten.eigentuemer_id,
            link_type: 'zuordnung',
          });
        }
      }

      if (changes.length > 0) {
        await supabase.from('ticket_historie').insert({
          ticket_id: aktuell.id,
          benutzer_id: userProfile?.id,
          aktion: 'bearbeitet',
          neuer_wert: `Geändert: ${changes.join(', ')}`,
        });
      }

      // Notify new assignee (nur einzelne Mitarbeiter — ein Team hat keinen einzelnen Empfänger)
      if (daten.zustaendig_id && zustTyp !== 'team' && daten.zustaendig_id !== aktuell.zustaendig_id && daten.zustaendig_id !== userProfile?.id) {
        await createNotification({
          benutzerId: daten.zustaendig_id,
          modul: 'tickets',
          typ: 'neue_zuweisung',
          nachricht: `Ticket ${aktuell.ticket_nummer} wurde Ihnen zugewiesen`,
          referenzId: aktuell.id,
          referenzTyp: 'ticket',
          linkPfad: '/admin/tickets',
          erstelltVon: userProfile?.id,
        });
      }

    }
  };

  const handleSave = async () => {
    setSaving(true);
    // Immer den LIVE-Stand nehmen (siehe Ref-Kommentar oben) — nie die Closure-Kopie.
    try {
      await schreibe(editDataRef.current);
      setEditing(false);
      try { await fetchMiniHistorie(); } catch (e) { console.warn('Mini-Historie Refresh fehlgeschlagen', e); }
      onUpdate();
    } catch (err: unknown) {
      console.error('Error saving ticket:', err);
      // Edit-Modus bleibt offen, damit die Eingaben nicht verloren gehen.
      alert('Speichern fehlgeschlagen: ' + (err instanceof Error ? err.message : (err as { message?: string })?.message || 'Unbekannter Fehler'));
    } finally {
      setSaving(false);
    }
  };

  // Stand des Tickets als Formularwerte (Grundlage für Teil-Speichern)
  const ausTicket = (t: Ticket): TicketEditData => ({
    titel: t.titel,
    beschreibung: t.beschreibung || '',
    prioritaet: t.prioritaet,
    kategorie_id: t.kategorie_id || '',
    zustaendig_id: t.zustaendig_id || '',
    zustaendig_typ: t.zustaendig_typ ?? null,
    faellig_am: t.faellig_am || '',
    wiedervorlage_am: t.wiedervorlage_am || '',
    wiedervorlage_notiz: t.wiedervorlage_notiz || '',
    objekt_id: t.objekt_id || '',
    mieter_id: t.mieter_id || '',
    eigentuemer_id: t.eigentuemer_id || '',
  });

  const speichereFelder = async (teil: Partial<TicketEditData>): Promise<boolean> => {
    setSaving(true);
    try {
      await schreibe({ ...ausTicket(ticketRef.current), ...teil });
      try { await fetchMiniHistorie(); } catch (e) { console.warn('Mini-Historie Refresh fehlgeschlagen', e); }
      onUpdate();
      return true;
    } catch (err) {
      console.error('Error saving ticket fields:', err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    editing,
    setEditing,
    saving,
    editData,
    setEditData,
    handleSave,
    speichereFelder,
    setzeStatus: (neuerStatus: string) => updateStatus(neuerStatus),
    handleStatusChange,
    abschlussZiel,
    showAbschluss,
    setShowAbschluss,
    abschlussGrund,
    setAbschlussGrund,
    handleAbschluss,
    handleAbschlussMitGrund: (grund: string) => { void updateStatus(abschlussZiel, grund); },
    miniHistorie,
    fetchMiniHistorie,
  };
}
